import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { brandToMergeFields, useBrandKit } from '../brand/BrandKitContext.tsx'
import { api, type TopicDetail } from '../lib/api.ts'
import { downloadBundleZip } from '../lib/bundle.ts'
import { slugForDepartment } from '../lib/departments.ts'
import { Breadcrumbs } from './Breadcrumbs.tsx'
import { ToolCard } from './ToolCard.tsx'

/** Artefact `type` -> the section it's grouped under, in display order. */
const GROUPS: { type: string; label: string }[] = [
  { type: 'policy', label: 'Policy' },
  { type: 'procedure', label: 'Procedures' },
  { type: 'form', label: 'Forms & Checklists' },
]

/** One content pack: a policy, its procedures and its forms, bundled by topic. */
export function TopicPage() {
  const { topicId = '' } = useParams()
  const { brand } = useBrandKit()
  const [topic, setTopic] = useState<TopicDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [bundling, setBundling] = useState(false)
  const [bundleError, setBundleError] = useState<string | null>(null)

  useEffect(() => {
    setTopic(null)
    setError(null)
    setBundleError(null)
    api.topic(topicId)
      .then(setTopic)
      .catch(() => setError('Could not load this topic. Please try again.'))
  }, [topicId])

  if (error) return <main className="catalog"><p className="notice notice--error">{error}</p></main>
  if (!topic) return <main className="catalog"><p className="notice">Loading…</p></main>

  const handleDownloadBundle = async () => {
    setBundling(true)
    setBundleError(null)
    try {
      const result = await api.bundle(topicId, brandToMergeFields(brand))
      await downloadBundleZip(result, brand)
    } catch {
      setBundleError('Could not build the bundle. Please try again.')
    } finally {
      setBundling(false)
    }
  }

  return (
    <main className="catalog">
      <Breadcrumbs
        items={[
          { label: 'Departments', to: '/' },
          { label: topic.department, to: `/departments/${slugForDepartment(topic.department)}` },
          { label: topic.title },
        ]}
      />

      {/* The department has moved into the trail; the Quality Area has not, and
          is the part an assessor asks about. */}
      <p className="eyebrow">{topic.quality_area}</p>
      <h1 className="tool__title">{topic.title}</h1>
      {topic.summary && <p className="tool__lead">{topic.summary}</p>}

      {GROUPS.map(({ type, label }) => {
        const artefacts = topic.artefacts.filter((a) => a.type === type)
        if (artefacts.length === 0) return null
        return (
          <section key={type} className="catalog__group">
            <h2 className="eyebrow">{label}</h2>
            <div className="catalog__grid">
              {artefacts.map((a) => (
                <ToolCard
                  key={a.id}
                  id={a.id}
                  to={`/topics/${topicId}/${a.id}`}
                  department={topic.department}
                  title={a.title}
                  isDraft={a.review_status !== 'approved'}
                />
              ))}
            </div>
          </section>
        )
      })}

      <div className="tool__actions topic__actions">
        <Link to={`/topics/${topicId}/knowledge-test`} className="btn btn--soft">Knowledge test</Link>
        <Link to={`/topics/${topicId}/meeting-guide`} className="btn btn--soft">Meeting guide</Link>
        <button type="button" className="btn btn--primary" disabled={bundling} onClick={handleDownloadBundle}>
          {bundling ? 'Preparing…' : 'Download whole bundle'}
        </button>
      </div>

      {bundleError && <p className="notice notice--error">{bundleError}</p>}
    </main>
  )
}
